import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  chaveDosEventosNfse,
  useManifestarNfse,
  type ManifestarNfsePayload,
  type NfseDocumento,
  type TipoManifestacaoNfse,
} from '@/services/ServicosNfse';
import { hasDateValue, hasManifestacaoFinalizada } from '../utils';

/** O limite do serviço de captura para a descrição do motivo (`manifestacao-nfse-dto.ts`). */
const LIMITE_DO_MOTIVO = 500;

/**
 * O código do motivo da rejeição continua o que a tela já mandava. O significado de cada código no leiaute nacional
 * não está em nenhum repositório lido [sem-fonte]; se a tela deve pedir o código, é a P.O. quem decide.
 */
const CODIGO_DO_MOTIVO = '1';

type Acao = 'confirmar' | 'rejeitar';

type Retorno = { tipo: 'sucesso' | 'erro'; mensagem: string };

interface ManifestacaoNfseDetalheProps {
  documento: NfseDocumento;
  chaveAcessoDoc: string;
  /**
   * A manifestação que o detalhe já registrou para esta nota. A linha aberta é a de quando o detalhe abriu, sem o
   * aceite: sem isto, reabrir o painel ofereceria de novo Confirmar e Rejeitar.
   */
  desfechoRegistrado?: TipoManifestacaoNfse | null;
  onManifestada?: (tipo: TipoManifestacaoNfse) => void;
}

function descreverDesfecho(documento: NfseDocumento) {
  if (hasDateValue(documento.DataCancelamento)) return 'Esta NFS-e foi cancelada.';
  if (hasDateValue(documento.DataRejeicao)) return 'O tomador já rejeitou esta NFS-e.';
  return 'O tomador já confirmou esta NFS-e.';
}

function descreverRegistro(tipo: TipoManifestacaoNfse) {
  return tipo === 'CONFIRMACAO'
    ? 'A confirmação desta NFS-e já foi registrada.'
    : 'A rejeição desta NFS-e já foi registrada.';
}

function AvisoDoRetorno({ retorno }: { retorno: Retorno }) {
  return (
    <Alert variant={retorno.tipo === 'erro' ? 'destructive' : 'default'}>
      {retorno.tipo === 'erro' ? <XCircle /> : <CheckCircle2 />}
      <AlertTitle>
        {retorno.tipo === 'erro' ? 'A manifestação não foi registrada' : 'Manifestação registrada'}
      </AlertTitle>
      <AlertDescription>
        <p className="break-words">{retorno.mensagem}</p>
      </AlertDescription>
    </Alert>
  );
}

/**
 * Manifestar, dentro do detalhe e só nas tomadas (SPEC-011, §1 decisão 5 e §3 item 7): confirmar, ou rejeitar com
 * motivo OBRIGATÓRIO — o botão de rejeitar não envia sem ele (T8). A resposta do serviço aparece aqui, como veio.
 *
 * A chamada continua direta ao serviço de captura: passar pelo servidor depende de decisão de outro sistema
 * ("O que para", item 1 da conferência).
 */
export function ManifestacaoNfseDetalhe({
  documento,
  chaveAcessoDoc,
  desfechoRegistrado,
  onManifestada,
}: ManifestacaoNfseDetalheProps) {
  const queryClient = useQueryClient();
  const manifestarNfse = useManifestarNfse();
  const [acao, setAcao] = useState<Acao | null>(null);
  const [motivo, setMotivo] = useState('');
  const [retorno, setRetorno] = useState<Retorno | null>(null);
  const [registrada, setRegistrada] = useState<TipoManifestacaoNfse | null>(null);

  const enviando = manifestarNfse.isPending;
  const motivoPreenchido = motivo.trim() !== '';
  /* A trava contra a segunda manifestação: registrada aqui, ou antes neste detalhe, Confirmar e Rejeitar saem e não
     voltam. A linha aberta não serve para isso — é a de quando o detalhe abriu, ainda sem o aceite. */
  const desfecho = registrada ?? desfechoRegistrado ?? null;

  if (hasManifestacaoFinalizada(documento)) {
    return <p className="text-sm text-muted-foreground">{descreverDesfecho(documento)}</p>;
  }

  if (desfecho) {
    return retorno ? (
      <AvisoDoRetorno retorno={retorno} />
    ) : (
      <p className="text-sm text-muted-foreground">{descreverRegistro(desfecho)}</p>
    );
  }

  const escolher = (nova: Acao | null) => {
    setAcao(nova);
    setRetorno(null);
  };

  const enviar = async () => {
    if (!acao || enviando) return;
    if (acao === 'rejeitar' && !motivoPreenchido) return;

    const tipoManifestacao: TipoManifestacaoNfse =
      acao === 'confirmar' ? 'CONFIRMACAO' : 'REJEICAO';
    const cnpjContribuinte = String(documento.CNPJ_Contribuinte || '').replace(/\D/g, '');
    const payload: ManifestarNfsePayload = {
      CNPJ_Contribuinte: cnpjContribuinte,
      chaveAcesso: chaveAcessoDoc,
      tipoManifestacao,
      cpfCnpjAutor: cnpjContribuinte,
      ambiente: 'PRODUCAO',
      ...(acao === 'rejeitar'
        ? { codigoMotivo: CODIGO_DO_MOTIVO, descricaoMotivo: motivo.trim() }
        : {}),
    };

    try {
      await manifestarNfse.mutateAsync(payload);
    } catch (erro) {
      setRetorno({
        tipo: 'erro',
        mensagem:
          erro instanceof Error && erro.message
            ? erro.message
            : 'Não foi possível registrar a manifestação da NFS-e.',
      });
      return;
    }

    /* Registrada: o desfecho fica guardado antes de qualquer outro passo, e as ações saem. */
    setRegistrada(tipoManifestacao);
    setRetorno({
      tipo: 'sucesso',
      mensagem:
        tipoManifestacao === 'CONFIRMACAO'
          ? 'O ambiente nacional registrou a confirmação desta NFS-e.'
          : 'O ambiente nacional registrou a rejeição desta NFS-e.',
    });
    setAcao(null);
    setMotivo('');
    onManifestada?.(tipoManifestacao);
    await queryClient.invalidateQueries({ queryKey: chaveDosEventosNfse(chaveAcessoDoc) });
  };

  return (
    <div className="space-y-4">
      {retorno && <AvisoDoRetorno retorno={retorno} />}

      {acao === null && (
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" onClick={() => escolher('confirmar')}>
            Confirmar a prestação
          </Button>
          <Button
            type="button"
            size="sm"
            variant="destructive"
            onClick={() => escolher('rejeitar')}
          >
            Rejeitar
          </Button>
        </div>
      )}

      {acao === 'confirmar' && (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            A confirmação vai ao ambiente nacional em nome do tomador.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" onClick={enviar} disabled={enviando}>
              {enviando && <Loader2 className="h-4 w-4 animate-spin" />}
              Enviar confirmação
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => escolher(null)}
              disabled={enviando}
            >
              Voltar
            </Button>
          </div>
        </div>
      )}

      {acao === 'rejeitar' && (
        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="motivo-rejeicao-nfse">Motivo da rejeição (obrigatório)</Label>
            <Textarea
              id="motivo-rejeicao-nfse"
              value={motivo}
              onChange={evento => setMotivo(evento.target.value)}
              maxLength={LIMITE_DO_MOTIVO}
              aria-invalid={!motivoPreenchido}
              aria-describedby="motivo-rejeicao-nfse-ajuda"
              disabled={enviando}
            />
            <p id="motivo-rejeicao-nfse-ajuda" className="text-xs text-muted-foreground">
              {motivoPreenchido
                ? `${motivo.length} de ${LIMITE_DO_MOTIVO} caracteres.`
                : 'Escreva o motivo para poder enviar a rejeição.'}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="destructive"
              onClick={enviar}
              disabled={enviando || !motivoPreenchido}
            >
              {enviando && <Loader2 className="h-4 w-4 animate-spin" />}
              Enviar rejeição
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => escolher(null)}
              disabled={enviando}
            >
              Voltar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
